import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeftOutlined, HomeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './CaseStudyPage.css';

const WarehouseManagement = () => {
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState('');
    const [readingProgress, setReadingProgress] = useState(0);

    // Table of contents (Results first)
    const tableOfContents = useMemo(() => [
        { id: 'results', title: 'Results & Impact' },
        { id: 'technologies', title: 'Technologies Used' },
        { id: 'overview', title: 'Project Overview' },
        { id: 'challenge', title: 'The Challenge' },
        { id: 'solution', title: 'Solution & Implementation' }
    ], []);

    // Scroll handling
    useEffect(() => {
        const handleScroll = () => {
            // Reading progress
            const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
            const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const scrolled = height > 0 ? (winScroll / height) * 100 : 0;
            setReadingProgress(scrolled);

            // Active section detection
            const sections = tableOfContents
                .map(item => document.getElementById(item.id))
                .filter(Boolean);

            const scrollPosition = window.scrollY + 200;
            for (let i = sections.length - 1; i >= 0; i--) {
                if (sections[i] && sections[i].offsetTop <= scrollPosition) {
                    setActiveSection(sections[i].id);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // initialize
        return () => window.removeEventListener('scroll', handleScroll);
    }, [tableOfContents]);

    const handleBackToCaseStudies = () => {
        navigate('/');
        setTimeout(() => {
            const mycaseSection = document.getElementById('mycase');
            if (mycaseSection) {
                mycaseSection.scrollIntoView({ behavior: 'auto', block: 'start' });
            }
        }, 50);
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <div className="case-study-page">
            {/* Reading progress bar */}
            <div className="reading-progress-container">
                <div
                    className="reading-progress-bar"
                    style={{ height: `${readingProgress}%` }}
                />
            </div>

            {/* Table of contents */}
            <div className="table-of-contents">
                <h4>Contents</h4>
                <ul>
                    {tableOfContents.map((item, idx) => (
                        <li key={item.id || idx}>
                            <button
                                className={`toc-item ${activeSection === item.id ? 'active' : ''}`}
                                onClick={() => scrollToSection(item.id)}
                            >
                                {item.title}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="case-study-container">
                {/* Header */}
                <div className="case-study-header">
                    {/* Breadcrumbs */}
                    <nav className="breadcrumb">
                        <button className="breadcrumb-item" onClick={() => navigate('/')}>
                            <HomeOutlined /> Home
                        </button>
                        <span className="breadcrumb-separator">/</span>
                        <button className="breadcrumb-item" onClick={handleBackToCaseStudies}>
                            Case Studies
                        </button>
                        <span className="breadcrumb-separator">/</span>
                        <span className="breadcrumb-current">Warehouse Management System</span>
                    </nav>

                    <button className="back-button" onClick={handleBackToCaseStudies}>
                        <ArrowLeftOutlined /> Back to Case Studies
                    </button>

                    <div className="case-study-hero">
                        <div className="case-study-image">
                            <img src="/images/case/logisticsWarehouse.jpg" alt="Warehouse Management System" />
                        </div>

                        <div className="case-study-info">
                            <h1 className="case-study-title">Warehouse Management System</h1>
                            <p className="case-study-subtitle">Logistics &amp; Supply Chain Optimization</p>

                            <div className="case-study-meta">
                                <div className="meta-item">
                                    <span className="meta-label">Duration:</span>
                                    {/* Resume dates: 2025/02–2025/08 ≈ 7 months */}
                                    <span className="meta-value">~7 months</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Team:</span>
                                    <span className="meta-value">Cross-functional delivery squad (BA + FE + Ops)</span>
                                </div>
                                <div className="meta-item">
                                    <span className="meta-label">Status:</span>
                                    <span className="meta-value status-live">Staged &amp; Production Deployed</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="case-study-content">
                    {/* Results & Impact */}
                    <section className="content-section" id="results">
                        <h2>Results &amp; Impact</h2>
                        <p>
                            Delivery focused on three outcome pillars: <strong>operational visibility</strong> (real-time SLA, volume, incident telemetry),
                            <strong> workflow reliability</strong> (system-triggered transitions replacing manual handovers),
                            and <strong>safe, repeatable releases</strong> (CI/CD with automated testing and rollbacks). These outcomes were realized in production via staged deployments.
                        </p>

                        <div className="results-grid">
                            <div className="result-card">
                                <div className="result-number">Live</div>
                                <div className="result-label">Production Deployment</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">SLA</div>
                                <div className="result-label">Real-time Monitoring</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Auto</div>
                                <div className="result-label">System-Triggered Handoffs</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Safe</div>
                                <div className="result-label">Rollback-Ready Releases</div>
                            </div>
                        </div>

                        {/* Mobile Results */}
                        <div className="mobile-results-row">
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Live</div>
                                </div>
                                <div className="mobile-result-label">Production</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">SLA</div>
                                </div>
                                <div className="mobile-result-label">Dashboard</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Auto</div>
                                </div>
                                <div className="mobile-result-label">Transitions</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Safe</div>
                                </div>
                                <div className="mobile-result-label">Rollback</div>
                            </div>
                        </div>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* Technologies */}
                    <section className="content-section" id="technologies">
                        <h2>Technologies Used</h2>
                        <div className="tech-stack">
                            <span className="tech-tag">React</span>
                            <span className="tech-tag">Node.js</span>
                            <span className="tech-tag">Open-source Dashboard Tool</span>
                            <span className="tech-tag">CI/CD Pipelines</span>
                            <span className="tech-tag">Cloud Servers (Staging/Prod)</span>
                            <span className="tech-tag">Automated Testing &amp; Rollback</span>
                            <span className="tech-tag">Version Control Workflow</span>
                        </div>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* Project Overview */}
                    <section className="content-section" id="overview">
                        <h2>Project Overview</h2>
                        <p>
                            A mid-sized logistics provider needed to replace a legacy parcel tracking and dispatch platform
                            that suffered from <strong>document parsing errors</strong>, lacked <strong>warehouse-system integration</strong>, and relied on
                            <strong> manual handovers</strong>. These issues degraded data reliability and obscured day-to-day operational visibility.
                            The engagement combined <em>consulting</em> (workflow discovery, target-state design) with <em>delivery</em>
                            (React + Node.js re-architecture, real-time dashboarding, and CI/CD + cloud deployments).
                        </p>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* The Challenge */}
                    <section className="content-section" id="challenge">
                        <h2>The Challenge</h2>
                        <p>
                            Primary pain points were rooted in inconsistent data entry and fragmented handoffs between physical warehouse activities and the system of record.
                            Without integrated checkpoints, managers lacked real-time signals on SLA adherence, throughput, and exceptions, and releases carried operational risk due to manual deployment practices.
                        </p>

                        <div className="challenge-grid">
                            <div className="challenge-item">
                                <h4>Parsing Errors</h4>
                                <p>Legacy parsing failures created rework and undermined data trust.</p>
                            </div>
                            <div className="challenge-item">
                                <h4>Manual Handoffs</h4>
                                <p>Physical-to-digital status updates were manual, error-prone, and slow.</p>
                            </div>
                            <div className="challenge-item">
                                <h4>No Warehouse Integration</h4>
                                <p>Warehouse operations weren’t linked to parcel records or workflow states.</p>
                            </div>
                            <div className="challenge-item">
                                <h4>Poor Visibility</h4>
                                <p>Managers lacked real-time SLA, volume, and incident telemetry.</p>
                            </div>
                        </div>
                    </section>

                    {/* Section Divider */}
                    <div className="section-divider"></div>

                    {/* Solution & Implementation */}
                    <section className="content-section" id="solution">
                        <h2>Solution &amp; Implementation</h2>
                        <p>
                            I executed a consulting-plus-delivery program that replaced manual handoffs with system-driven events, modernized the UI for operations staff,
                            and introduced safe, repeatable release practices. Claims below align with the resume and preserve client confidentiality.
                        </p>

                        <div className="solution-features">
                            <div className="feature-item">
                                <h4>End-to-End Workflow Redesign</h4>
                                <p>
                                    Mapped intake → verification → sorting → staging → dispatch → exception handling; defined digital checkpoints that mirror physical handling,
                                    enabling <strong>system-triggered task transitions</strong> instead of manual pass-offs.
                                </p>
                            </div>

                            {/* Simple Divider */}
                            {/* <div className="section-divider simple"></div> */}

                            <div className="feature-item">
                                <h4>UI Modernization (React)</h4>
                                <p>
                                    Rebuilt internal screens for dynamic parcel tracking, streamlined forms that previously caused parsing issues, and role-appropriate views for floor
                                    operations and coordinators.
                                </p>
                            </div>

                            {/* Simple Divider */}
                            {/* <div className="section-divider simple"></div> */}

                            <div className="feature-item">
                                <h4>Service Layer (Node.js)</h4>
                                <p>
                                    Implemented re-architected logic/services coordinating parcel status changes and aligning the system of record with warehouse events.
                                </p>
                            </div>

                            {/* Simple Divider */}
                            {/* <div className="section-divider simple"></div> */}

                            <div className="feature-item">
                                <h4>Real-time Operations Dashboard</h4>
                                <p>
                                    Delivered with an open-source tool, surfacing <strong>SLA compliance</strong>, <strong>delivery volume</strong>, and <strong>incident rates</strong> for proactive management.
                                </p>
                            </div>

                            {/* Simple Divider */}
                            {/* <div className="section-divider simple"></div> */}

                            <div className="feature-item">
                                <h4>CI/CD on Cloud</h4>
                                <p>
                                    Set up CI/CD with automated testing, version control discipline, and <strong>staging/production</strong> environments on cloud servers, including
                                    <strong> rollback capabilities</strong> for safe releases.
                                </p>
                            </div>
                        </div>

                        {/* Section Divider */}
                        <div className="section-divider"></div>

                        <h2>Results &amp; Impact</h2>
                        <p>
                            The new platform provides verifiable visibility for managers, replaces fragile manual handoffs with system events, and institutionalizes safe deployments through CI/CD.
                            These results are qualitative as the resume does not state numeric uplifts.
                        </p>

                        {/* <div className="results-grid">
                            <div className="result-card">
                                <div className="result-number">Live</div>
                                <div className="result-label">Production Deployment</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">SLA</div>
                                <div className="result-label">Real-time Monitoring</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Auto</div>
                                <div className="result-label">System-Triggered Handoffs</div>
                            </div>
                            <div className="result-card">
                                <div className="result-number">Safe</div>
                                <div className="result-label">Rollback-Ready Releases</div>
                            </div>
                        </div> */}

                        {/* <div className="mobile-results-row">
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Live</div>
                                </div>
                                <div className="mobile-result-label">Production</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">SLA</div>
                                </div>
                                <div className="mobile-result-label">Dashboard</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Auto</div>
                                </div>
                                <div className="mobile-result-label">Transitions</div>
                            </div>
                            <div className="mobile-result-circle">
                                <div className="mobile-result-ball">
                                    <div className="mobile-result-number">Safe</div>
                                </div>
                                <div className="mobile-result-label">Rollback</div>
                            </div>
                        </div> */}
                    </section>

                    {/* Back to Case Studies at the end */}
                    <div style={{ textAlign: 'left', margin: '0px 0', paddingTop: '20px', borderTop: '1px solid #e8e8e8' }}>
                        <button className="back-button" onClick={handleBackToCaseStudies}>
                            <ArrowLeftOutlined /> Back to Case Studies
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WarehouseManagement;
